"use client";

import { ChevronDown } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils/cn";
import { useOverlayPosition } from "./use-overlay-position";

const DropdownContext = createContext<(() => void) | null>(null);

export type DropdownMenuProps = {
  trigger: ReactNode;
  children: ReactNode;
  align?: "left" | "right";
  className?: string;
  menuClassName?: string;
  ariaLabel?: string;
  disabled?: boolean;
};

export function DropdownMenu({ trigger, children, align = "left", className, menuClassName, ariaLabel = "Actions", disabled }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const position = useOverlayPosition(open, rootRef, align === "right" ? "bottom-end" : "bottom-start", 224);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) close();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [close, open]);

  return (
    <div ref={rootRef} className={cn("relative inline-block", className)}>
      <button
        type="button"
        className="inline-flex items-center gap-ds-2 rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/25"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        {trigger}
        <ChevronDown className={cn("h-4 w-4 text-muted transition-transform duration-fast", open && "rotate-180")} aria-hidden />
      </button>
      {open && position && typeof document !== "undefined"
        ? createPortal(
            <DropdownContext.Provider value={close}>
              <div
                ref={menuRef}
                className={cn("overflow-y-auto rounded-card border border-card-border bg-surface-elevated p-ds-1 shadow-overlay", menuClassName)}
                style={position}
                role="menu"
                aria-label={ariaLabel}
              >
                {children}
              </div>
            </DropdownContext.Provider>,
            document.body,
          )
        : null}
    </div>
  );
}

export type DropdownItemProps = ButtonHTMLAttributes<HTMLButtonElement>;

export function DropdownItem({ children, onClick, className, ...props }: DropdownItemProps) {
  const close = useContext(DropdownContext);
  return (
    <button
      type="button"
      className={cn(
        "flex min-h-9 w-full items-center rounded-control px-ds-3 py-ds-2 text-left text-caption text-foreground transition-colors duration-fast hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-45",
        className,
      )}
      role="menuitem"
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) close?.();
      }}
      {...props}
    >
      {children}
    </button>
  );
}
